import React, { useEffect, useMemo, useState } from "react";
import { FormSAR } from "./styles";
import { FaChevronDown, FaChevronUp, FaTrashAlt, FaRegCopy } from "react-icons/fa";
import { IoIosSave } from "react-icons/io";
import Select from "react-select";
import Api, { exceptionNotificationAPI } from '../../services/api';
import TableLoading from "../../components/TableLoading";
import Swal from 'sweetalert2';

const rid = () => Math.random().toString(36).slice(2, 10);
const TYPE_WITHOUT_VALUE = [1, 5];

function emptySingle() {
    return {
        id: rid(),
        label: undefined,
        collapsed: false,
        editable: true,
        kpi: null,
        type: "Simple",
        typeValue: null,
        ruleTemplate: null,
        lowerLimit: null,
        upperLimit: null,
        comparator: null,
        linkedKPI: null,
        value: 0,
        excludedCompanies: [],
    };
}

function emptyCompound() {
    return {
        id: rid(),
        logicOperator: 1,
        collapsed: false,
        editable: true,
        rules: [emptySingle()],
    };
}

function emptyBlock() {
    return {
        id: rid(),
        logicOperator: 1,
        collapsed: false,
        editable: true,
        discussion:
            "Could you please give us more details about the main reasons for this?",
        children: [
            { kind: "single", item: emptySingle() },
            { kind: "compound", item: emptyCompound() },
        ],
    };
}

function cloneSingle(s) { return { ...s, id: rid() }; }
function cloneCompound(c) {
    return {
        ...c, // copia TODOS os campos (KPI, type, discussion, etc)

        id: rid(), 
        compoundRuleRegisterID: null, //evita sobrescrever no backend

        // garantir que as regras internas também sejam clonadas corretamente
        rules: (c.rules || []).map(cloneSingle)};}
function cloneBlock(b) {
    return {
        ...b, // copia tudo

        id: rid(),
        blockRuleRegisterID: null, // evita sobrescrever no backend

        children: (b.children || []).map((ch) => (
            ch.kind === "single"
                ? { kind: "single", item: cloneSingle(ch.item) }
                : { kind: "compound", item: cloneCompound(ch.item) }
        )),
    };
}

function singleSummary(r, rulesList = [], kpiList = []) {
    // Opções para mapear valores para descrições
    const typeOptions = [
        { value: 1, label: "Simple" },
        { value: 2, label: "Country" },
        { value: 3, label: "FuelType" },
        { value: 4, label: "CultureType" },
        { value: 5, label: "Year" },
        { value: 6, label: "Curing Barn" }
    ];

    const operatorOptions = [
        { value: 1, label: "+-" },
        { value: 2, label: "<" },
        { value: 3, label: "<>" },
        { value: 4, label: "=" },
        { value: 5, label: ">=" },
        { value: 6, label: "<=" },
        { value: 7, label: ">" }
    ];

    // Função auxiliar para encontrar descrição por valor
    const getDescription = (options, value) => {
        if (!value) return "N/A";
        const option = options.find(opt => opt.value === value);
        return option ? option.label : value.toString();
    };

    // 1. Mapear KPI - extrair apenas o refNumber (o que está entre [])
    const kpiDesc = (() => {
        if (!r.kpi) return "[KPI]";
        const kpi = kpiList.find(k => k.value === r.kpi);
        if (kpi && kpi.label) {
            // Extrair apenas o refNumber do label (ex: "[123] Description" -> "[123]")
            const refMatch = kpi.label.match(/^\[(\d+)\]/);
            return refMatch ? `[${refMatch[1]}]` : `[${kpi.label}]`;
        }
        return `[${r.kpi}]`;
    })();

    // 2. Mapear TypeValue e concatenar se existir
    const typeValueDesc = (() => {
        if (!r.typeValue) return "";
        const typeDesc = getDescription(typeOptions, r.type);
        return `${typeDesc}: ${r.typeValue}`;
    })();

    // 3. Mapear Rule - buscar descrição na lista de regras
    const ruleDesc = (() => {
        if (!r.ruleTemplate) return "Rule";
        const rule = rulesList.find(rule => rule.value === r.ruleTemplate);
        return rule ? rule.label : `Rule ${r.ruleTemplate}`;
    })();

    // 4. Mapear Comparator
    const comparatorDesc = getDescription(operatorOptions, r.comparator);

    // 5. Mapear Value com validações hierárquicas
    const valueDesc = (() => {
        // Prioridade 1: Se tem value, usar value
        if (r.value !== null && r.value !== undefined && r.value !== "") {
            return `${comparatorDesc} ${r.value}`;
        }

        // Prioridade 2: Se não tem value, verificar Upper e LowerLimit
        const hasLowerLimit = r.lowerLimit !== null && r.lowerLimit !== undefined && r.lowerLimit !== "";
        const hasUpperLimit = r.upperLimit !== null && r.upperLimit !== undefined && r.upperLimit !== "";

        if (hasLowerLimit || hasUpperLimit) {
            const limits = [];
            if (hasLowerLimit) limits.push(r.lowerLimit);
            if (hasUpperLimit) limits.push(r.upperLimit);
            return `Range: ${limits.join(" - ")}`;
        }

        // Prioridade 3: Se não tem limits, verificar LinkedKPI
        if (r.linkedKPI !== null && r.linkedKPI !== undefined && r.linkedKPI !== "") {
            return `Linked: ${r.linkedKPI}`;
        }

        // Fallback: mostrar apenas o operator
        return comparatorDesc;
    })();

    // Construir array de partes, filtrando valores vazios
    const parts = [kpiDesc];

    // Adicionar TypeValue se existir
    if (typeValueDesc) {
        parts.push(typeValueDesc);
    }

    // Adicionar Rule
    parts.push(ruleDesc);

    // Adicionar Value/Operator
    parts.push(valueDesc);

    return parts.join(" · ");
}

// ---------------- Primitivos de UI ----------------
function OperatorToggle({ value, onChange, fieldName = "logicOperator", editable = true }) {
    const handleOperatorChange = (op) => {
        if (typeof onChange === 'function' && editable) {
            // Sempre usa o formato de objeto com fieldName
            onChange({ [fieldName]: op.value });
        }
    };

    return (
        <div className={`toggle ${!editable ? 'disabled' : ''}`} role="group" aria-label="Logical operator">
            {[{ "value": 1, "label": "AND" }, { "value": 2, "label": "OR" }].map((op) => (
                <button
                    key={op.label}
                    className={value === op.value ? "active" : ""}
                    onClick={() => handleOperatorChange(op)}
                    type="button"
                    disabled={!editable}
                >
                    {op.label}
                </button>
            ))}
        </div>
    );
}

// Helper function para gerenciar onChange do Operator
function createOperatorSelectProps(fieldName, value, onChange) {
    return {
        value: value[fieldName] || "",
        onChange: (e) => onChange({ [fieldName]: e.target.value })
    };
}

function HeaderActions({ collapsed, onToggle, onCopy, onDelete, onSave, value }) {
    return (
        <div className="row-actions">
            <button className="btn btn-ghost btn-icon" title="Save" onClick={() => onSave(value)} type="button"><IoIosSave size={20} color="#FFF" /></button>
            <button className="btn btn-ghost btn-icon" title="Copy" onClick={onCopy} type="button"><FaRegCopy size={15} color="#FFF" /></button>
            <button className="btn btn-ghost btn-icon" title="Delete" onClick={onDelete} type="button"><FaTrashAlt size={15} color="#FFF" /></button>
            <button className="btn btn-ghost btn-icon" title={collapsed ? "Expand" : "Collapse"} onClick={onToggle} type="button">
                {collapsed ? <FaChevronUp size={15} color="#FFF" /> : <FaChevronDown size={15} color="#FFF" />}
            </button>
        </div>
    );
}

// ---------------- Single Rule ----------------
function SingleRuleCard({ value, onChange, onCopy, onDelete, onSave, ruleList, typesList, KPIList, typeValuesList, operatorOptions, companiesOptions, isInsideBlock = false, isInsideCompound = false, inheritedExcluded = [] }) {
    const selectedCompanies = (companiesOptions || []).filter(o =>
        (value.excludedCompanies || []).includes(o.value)
    );

    const inherited = inheritedExcluded || [];
    const own = value.excludedCompanies || [];
    const allSelected = Array.from(new Set([...inherited, ...own]));

    const set = (patch) => onChange({ ...value, ...patch });

    // Helper function para Select components
    const createSelectProps = (fieldName, options) => ({
        value: options && Array.isArray(options) ? options.find(option => option.value === value[fieldName]) || null : null,
        onChange: (selectedOption) => set({ [fieldName]: selectedOption ? selectedOption.value : null }),
        isDisabled: fieldName === 'comparator' ? isFieldDisabled('comparator') : !value.editable
    });

    // Helper function específica para o campo KPI com auto-população do Type
    const createKpiSelectProps = (options) => ({
        value: options && Array.isArray(options) ? options.find(option => option.value === value.kpi) || null : null,
        onChange: (selectedOption) => {
            const kpiValue = selectedOption ? selectedOption.value : null;
            const updates = { kpi: kpiValue };

            // Se um KPI foi selecionado e tem columnType "Simple", auto-popular o Type
            if (selectedOption && TYPE_WITHOUT_VALUE.includes(selectedOption.columnType)) {
                updates.type = selectedOption.columnType;
                updates.typeValue = null;
            }

            set(updates);
        }
    });

    // Lógica condicional para Type Options baseada no KPI selecionado
    const getFilteredTypeOptions = () => {
        if (!KPIList || !Array.isArray(KPIList)) return typesList || [];

        const selectedKPI = KPIList.find(kpi => kpi.value === value.kpi);
        if (!selectedKPI) return typesList || [];

        // Se o KPI tem columnType, filtra os tipos baseado nisso
        if (selectedKPI.columnType) {
            return (typesList || []).filter(type => type.value === selectedKPI.columnType);
        }

        return typesList || [];
    };

    // Lógica condicional para Type Values baseada no Type selecionado
    const getFilteredTypeValues = () => {
        if (!typeValuesList || !Array.isArray(typeValuesList) || !value.type) return [];

        return typeValuesList
            .filter(typeValue => typeValue.typeID === value.type)
            .map(typeValue => ({
                value: typeValue.typeValueID,
                label: typeValue.description
            }));
    };

    // Lógica para determinar se deve mostrar Type Value
    const shouldShowTypeValue = () => {
        return value.type && !TYPE_WITHOUT_VALUE.includes(value.type);
    };

    // Obter o objeto da regra selecionada com suas flags de validação
    const getSelectedRuleObject = () => {
        if (!value.ruleTemplate || !ruleList || !Array.isArray(ruleList)) return null;
        return ruleList.find(rule => rule.value === value.ruleTemplate);
    };

    // Lógica para determinar se um campo deve ser bloqueado baseado na regra selecionada
    const isFieldDisabled = (fieldName) => {
        if (!value.editable) return true; // Sempre desabilitado se não for editável

        const ruleObject = getSelectedRuleObject();
        if (!ruleObject) return false; // Se não há regra selecionada, não bloqueia

        switch (fieldName) {
            case 'lowerLimit':
                return !ruleObject.allowLowerLimit;
            case 'upperLimit':
                return !ruleObject.allowUpperLimit;
            case 'comparator':
                return !ruleObject.allowComparisonOperator;
            case 'value':
                return !ruleObject.allowTargetValue;
            case 'linkedKPI':
                return !ruleObject.allowKpiLink;
            default:
                return false;
        }
    };

    return (
        <div className="card" id={`single-${value.id}`}>
            <div className="card-hd card-hd-blue">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="badge badge-title">{value.ruleRegisterID} - Condition</span>
                    <span className="small">{value.collapsed ? singleSummary(value, ruleList, KPIList) : value.label || value.id}</span>
                </div>
                <div className="row-actions">
                    <label className="small" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input type="checkbox" checked={!!value.editable} onChange={(e) => set({ editable: e.target.checked })} />
                        Editable
                    </label>

                    <HeaderActions collapsed={value.collapsed} onToggle={() => set({ collapsed: !value.collapsed })} onCopy={onCopy} onDelete={onDelete} onSave={onSave} value={value} />
                </div>
            </div>

            {!value.collapsed && (
                <div className="card-ct container">
                    {/* Primeira linha: KPI, Type, TypeValue */}
                    <div className="row">
                        <div className="col-md-4 pt-4">
                            <label className="small">KPI</label>
                            <Select
                                name="kpi"
                                className="basic-select"
                                classNamePrefix="select"
                                options={KPIList || []}
                                isDisabled={!value.editable}
                                {...createKpiSelectProps(KPIList)}
                            />
                        </div>
                        <div className="col-md-4 pt-4">
                            <label className="small">Type</label>
                            <Select
                                name="type"
                                className="basic-select"
                                classNamePrefix="select"
                                options={getFilteredTypeOptions()}
                                isDisabled={!value.editable}
                                {...createSelectProps('type', getFilteredTypeOptions())}
                            />
                        </div>
                        {shouldShowTypeValue() && (
                            <div className="col-md-4 pt-4">
                                <label className="small">Type Value</label>
                                <Select
                                    name="typeValue"
                                    className="basic-select"
                                    classNamePrefix="select"
                                    options={getFilteredTypeValues()}
                                    isDisabled={!value.editable}
                                    {...createSelectProps('typeValue', getFilteredTypeValues())}
                                />
                            </div>
                        )}
                    </div>

                    {/* Segunda linha: Rule, LowerLimit, UpperLimit, Operator, LinkedKPI, Value */}
                    <div className="row">
                        <div className="col-md-4 pt-4">
                            <label className="small">Rule</label>
                            <Select
                                name="rule"
                                options={ruleList || []}
                                className="basic-select"
                                classNamePrefix="select"
                                isDisabled={!value.editable}
                                {...createSelectProps('ruleTemplate', ruleList)}
                            />
                        </div>
                        <div className="col-md-1 pt-4">
                            <label className="small">Lower Limit</label>
                            <input
                                type="number"
                                disabled={isFieldDisabled('lowerLimit')}
                                value={value.lowerLimit ?? ""}
                                onChange={(e) => set({ lowerLimit: e.target.value === "" ? null : parseFloat(e.target.value) })}
                            />
                        </div>
                        <div className="col-md-1 pt-4">
                            <label className="small">Upper Limit</label>
                            <input
                                type="number"
                                disabled={isFieldDisabled('upperLimit')}
                                value={value.upperLimit ?? ""}
                                onChange={(e) => set({ upperLimit: e.target.value === "" ? null : parseFloat(e.target.value) })}
                            />
                        </div>
                        <div className="col-md-2 pt-4">
                            <label className="small">Operator</label>
                            <Select
                                name="comparator"
                                className="basic-select"
                                classNamePrefix="select"
                                options={operatorOptions}
                                {...createSelectProps('comparator', operatorOptions)}
                            />
                        </div>
                        <div className="col-md-2 pt-4">
                            <label className="small">Linked KPI</label>
                            <textarea
                                rows={1}
                                disabled={isFieldDisabled('linkedKPI')}
                                value={value.linkedKPI ?? ""}
                                onChange={(e) => set({ linkedKPI: e.target.value })}
                            />
                        </div>
                        <div className="col-md-2 pt-4">
                            <label className="small">Value</label>
                            <textarea
                                rows={1}
                                disabled={isFieldDisabled('value')}
                                value={value.value ?? ""}
                                onChange={(e) => set({ value: e.target.value })}
                            />
                        </div>
                    </div>
                    {!isInsideBlock && !isInsideCompound && (
                        <>
                            <div className="sep" />
                            <div className="row">
                                <div className="col-md-12 pt-4">
                                    <label className="small">Discussion</label>
                                    <textarea rows={2} disabled={!value.editable} value={value.discussion || ""} onChange={(e) => set({ discussion: e.target.value })} />
                                </div>
                            </div>
                        </>
                    )}
                    <div className="row">
                        <div className="col-md-12 pt-4">
                            <label className="small">Companies to exclude</label>
                            <Select
                               name="excludedCompanies"
                               options={companiesOptions}
                               isMulti
                               isDisabled={!value.editable}
                               value={(companiesOptions || []).filter(o =>
                                    allSelected.includes(o.value))}
                                    onChange={(selected) => {
                                        const selectedIds = selected ? selected.map(s => s.value) : [];

                                        // remove herdados → salva só o que é próprio
                                        const ownOnly = selectedIds.filter(id => !inherited.includes(id));

                                         set({ excludedCompanies: ownOnly });
                                    }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------- Compound Rule ----------------
function CompoundRuleCard({ value, onChange, onCopy, onDelete, onAddSingle, onSave, onSaveSingle, ruleList, typesList, KPIList, typeValuesList, operatorOptions, companiesOptions, isInsideBlock = false, inheritedExcluded = [] }) {
    const selectedCompanies = (companiesOptions || []).filter(o =>
        (value.excludedCompanies || []).includes(o.value)
    );
    const set = (patch) => onChange({ ...value, ...patch });

    // Helper function para Select components
    const createSelectProps = (fieldName, options) => ({
        value: options && Array.isArray(options) ? options.find(option => option.value === value[fieldName]) || null : null,
        onChange: (selectedOption) => set({ [fieldName]: selectedOption ? selectedOption.value : null })
    });

    // Helper function específica para o campo KPI com auto-população do Type
    const createKpiSelectProps = (options) => ({
        value: options && Array.isArray(options) ? options.find(option => option.value === value.targetSubSectionKPIID) || null : null,
        onChange: (selectedOption) => {
            const kpiValue = selectedOption ? selectedOption.value : null;
            const updates = { targetSubSectionKPIID: kpiValue };

            // Se um KPI foi selecionado e tem columnType "Simple", auto-popular o Type
            if (selectedOption && TYPE_WITHOUT_VALUE.includes(selectedOption.columnType)) {
                updates.targetTypeID = selectedOption.columnType;
                updates.targetTypeValueID = null;
            }

            set(updates);
        }
    });

    // Lógica condicional para Type Options baseada no KPI selecionado
    const getFilteredTypeOptions = () => {
        if (!KPIList || !Array.isArray(KPIList)) return typesList || [];

        const selectedKPI = KPIList.find(kpi => kpi.value === value.kpi);
        if (!selectedKPI) return typesList || [];

        // Se o KPI tem columnType, filtra os tipos baseado nisso
        if (selectedKPI.columnType) {
            return (typesList || []).filter(type => type.value === selectedKPI.columnType);
        }

        return typesList || [];
    };

    // Lógica condicional para Type Values baseada no Type selecionado
    const getFilteredTypeValues = () => {
        if (!typeValuesList || !Array.isArray(typeValuesList) || !value.targetTypeID) return [];

        return typeValuesList
            .filter(typeValue => typeValue.typeID === value.targetTypeID)
            .map(typeValue => ({
                value: typeValue.typeValueID,
                label: typeValue.description
            }));
    };

    // Lógica para determinar se deve mostrar Type Value
    const shouldShowTypeValue = () => {
        return value.targetTypeID && !TYPE_WITHOUT_VALUE.includes(value.targetTypeID);
    };

    return (
        <div className="card" id={`compound-${value.id}`}>
            <div className="card-hd card-hd-blue">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="badge badge-title">{value.compoundRuleRegisterID} - Compound Rule</span>
                    <OperatorToggle value={value.logicOperator} onChange={set} fieldName="logicOperator" editable={value.editable} />
                </div>
                <div className="row-actions">
                    <button className="btn btn-outline btn-small" onClick={onAddSingle} type="button">+ Add Single Rule</button>

                    <label className="checkbox-label">
                        <input type="checkbox" checked={!!value.editable} onChange={(e) => set({ editable: e.target.checked })} />
                        Editable
                    </label>

                    <HeaderActions collapsed={value.collapsed} onToggle={() => set({ collapsed: !value.collapsed })} onCopy={onCopy} onDelete={onDelete} onSave={onSave} value={value} />
                </div>
            </div>

            {!value.collapsed && (
                <div className="card-ct">
                    {!isInsideBlock && (
                        <>
                            <div className="container">
                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="small">KPI</label>
                                        <Select
                                            name="targetSubSectionKPIID"
                                            className="basic-select"
                                            classNamePrefix="select"
                                            options={KPIList || []}
                                            isDisabled={!value.editable}
                                            {...createKpiSelectProps(KPIList)}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="small">Type</label>
                                        <Select
                                            name="targetTypeID"
                                            className="basic-select"
                                            classNamePrefix="select"
                                            options={getFilteredTypeOptions()}
                                            isDisabled={!value.editable}
                                            {...createSelectProps('targetTypeID', getFilteredTypeOptions())}
                                        />
                                    </div>
                                    {shouldShowTypeValue() && (
                                        <div className="col-md-2">
                                            <label className="small">Type Value</label>
                                            <Select
                                                name="targetTypeValueID"
                                                className="basic-select"
                                                classNamePrefix="select"
                                                options={getFilteredTypeValues()}
                                                isDisabled={!value.editable}
                                                {...createSelectProps('targetTypeValueID', getFilteredTypeValues())}
                                            />
                                        </div>
                                    )}
                                    <div className={"col-md-" + (shouldShowTypeValue() ? "4" : "6")}>
                                        <label className="small">Discussion</label>
                                        <textarea rows={2} disabled={!value.editable} value={value.discussion || ""} onChange={(e) => set({ discussion: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <div className="container">
                                <div className="row">
                                    <div className="col-md-12">
                                        <label className="small">Companies to exclude</label>
                                        <Select
                                            name="excludedCompanies"
                                            options={companiesOptions}
                                            isMulti
                                            isDisabled={!value.editable}
                                            value={selectedCompanies}
                                            onChange={(selected) =>
                                                set({
                                                    excludedCompanies: selected
                                                        ? selected.map(s => s.value)
                                                        : []
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="sep" />
                        </>
                    )}
                    <div className="grid" style={{ gap: 10 }}>
                        {value.rules.map((r) => (
                            <SingleRuleCard
                                key={r.id}
                                value={{ ...r, editable: value.editable && r.editable }}
                                onChange={(nr) => set({ rules: value.rules.map((x) => (x.id === r.id ? nr : x)) })}
                                onCopy={() => set({ rules: [...value.rules, cloneSingle(r)] })}
                                onDelete={() => set({ rules: value.rules.filter((x) => x.id !== r.id) })}
                                onSave={(ruleData) => onSaveSingle && onSaveSingle(ruleData)}
                                ruleList={ruleList}
                                typesList={typesList}
                                KPIList={KPIList}
                                typeValuesList={typeValuesList}
                                operatorOptions={operatorOptions}
                                companiesOptions={companiesOptions}
                                isInsideBlock={isInsideBlock}
                                isInsideCompound={true}
                                 inheritedExcluded={[
                                    ...(inheritedExcluded || []),
                                    ...(value.excludedCompanies || [])
                                ]}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------- Block ----------------
function BlockCard({ value, onChange, onCopy, onDelete, onAddSingle, onAddCompound, onSave, onSaveSingle, onSaveCompound, ruleList, typesList, KPIList, typeValuesList, operatorOptions, companiesOptions }) {
    const selectedCompanies = (companiesOptions || []).filter(o =>
        (value.excludedCompanies || []).includes(o.value)
    );
    
    const set = (patch) => onChange({ ...value, ...patch });

    // Helper function para Select components
    const createSelectProps = (fieldName, options) => ({
        value: options && Array.isArray(options) ? options.find(option => option.value === value[fieldName]) || null : null,
        onChange: (selectedOption) => set({ [fieldName]: selectedOption ? selectedOption.value : null })
    });

    // Helper function específica para o campo KPI com auto-população do Type
    const createKpiSelectProps = (options) => ({
        value: options && Array.isArray(options) ? options.find(option => option.value === value.targetSubSectionKPIID) || null : null,
        onChange: (selectedOption) => {
            const kpiValue = selectedOption ? selectedOption.value : null;
            const updates = { targetSubSectionKPIID: kpiValue };

            // Se um KPI foi selecionado e tem columnType "Simple", auto-popular o Type
            if (selectedOption && TYPE_WITHOUT_VALUE.includes(selectedOption.columnType)) {
                updates.targetTypeID = selectedOption.columnType;
                updates.targetTypeValueID = null;
            }

            set(updates);
        }
    });

    // Lógica condicional para Type Options baseada no KPI selecionado
    const getFilteredTypeOptions = () => {
        if (!KPIList || !Array.isArray(KPIList)) return typesList || [];

        const selectedKPI = KPIList.find(kpi => kpi.value === value.kpi);
        if (!selectedKPI) return typesList || [];

        // Se o KPI tem columnType, filtra os tipos baseado nisso
        if (selectedKPI.columnType) {
            return (typesList || []).filter(type => type.value === selectedKPI.columnType);
        }

        return typesList || [];
    };

    // Lógica condicional para Type Values baseada no Type selecionado
    const getFilteredTypeValues = () => {
        if (!typeValuesList || !Array.isArray(typeValuesList) || !value.targetTypeID) return [];

        return typeValuesList
            .filter(typeValue => typeValue.typeID === value.targetTypeID)
            .map(typeValue => ({
                value: typeValue.typeValueID,
                label: typeValue.description
            }));
    };

    // Lógica para determinar se deve mostrar Type Value
    const shouldShowTypeValue = () => {
        return value.targetTypeID && !TYPE_WITHOUT_VALUE.includes(value.targetTypeID);
    };

    return (
        <div className="card" id={`block-${value.id}`}>
            <div className="card-hd card-hd-blue">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="badge badge-title">{value.blockRuleRegisterID} - Block</span>
                    <OperatorToggle value={value.logicOperator} onChange={set} fieldName="logicOperator" editable={value.editable} />
                </div>
                <div className="row-actions">
                    <button className="btn btn-outline btn-small" onClick={onAddCompound} type="button">+ Add Compound Rule</button>
                    <button className="btn btn-outline btn-small" onClick={onAddSingle} type="button">+ Add Single Rule</button>

                    <label className="checkbox-label">
                        <input type="checkbox" checked={!!value.editable} onChange={(e) => set({ editable: e.target.checked })} />
                        Editable
                    </label>

                    <HeaderActions collapsed={value.collapsed} onToggle={() => set({ collapsed: !value.collapsed })} onCopy={onCopy} onDelete={onDelete} onSave={onSave} value={value} />
                </div>
            </div>

            {!value.collapsed && (
                <div className="card-ct">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-4">
                                <label className="small">KPI</label>
                                <Select
                                    name="targetSubSectionKPIID"
                                    className="basic-select"
                                    classNamePrefix="select"
                                    options={KPIList || []}
                                    isDisabled={!value.editable}
                                    {...createKpiSelectProps(KPIList)}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="small">Type</label>
                                <Select
                                    name="targetTypeID"
                                    className="basic-select"
                                    classNamePrefix="select"
                                    options={getFilteredTypeOptions()}
                                    isDisabled={!value.editable}
                                    {...createSelectProps('targetTypeID', getFilteredTypeOptions())}
                                />
                            </div>
                            {shouldShowTypeValue() && (
                                <div className="col-md-2">
                                    <label className="small">Type Value</label>
                                    <Select
                                        name="targetTypeValueID"
                                        className="basic-select"
                                        classNamePrefix="select"
                                        options={getFilteredTypeValues()}
                                        isDisabled={!value.editable}
                                        {...createSelectProps('targetTypeValueID', getFilteredTypeValues())}
                                    />
                                </div>
                            )}
                            <div className={"col-md-" + (shouldShowTypeValue() ? "4" : "6")}>
                                <label className="small">Discussion</label>
                                <textarea rows={2} disabled={!value.editable} value={value.discussion || ""} onChange={(e) => set({ discussion: e.target.value })} />
                            </div>
                        </div>
                    </div>
                    <div className="container">
                        <div className="row">
                                <div className="col-md-12">
                                    <label className="small">Companies to exclude</label>
                                    <Select
                                        name="excludedCompanies"
                                        options={companiesOptions}
                                        isMulti
                                        isDisabled={!value.editable}
                                        value={selectedCompanies}
                                        onChange={(selected) =>
                                            set({
                                                excludedCompanies: selected
                                                    ? selected.map(s => s.value)
                                                    : []
                                             })
                                        }
                                    />
                                </div>
                            </div>
                         </div>
                    <div className="sep" />
                    <div className="grid" style={{ gap: 10 }}>
                        {value.children.map((ch) => (
                            <div key={(ch.kind === "single" ? ch.item.id : ch.item.id) + ch.kind}>
                                {ch.kind === "single" ? (
                                    <SingleRuleCard
                                        value={{ ...ch.item, editable: value.editable && ch.item.editable }}
                                        onChange={(nr) => set({ children: value.children.map((x) => (x.kind === "single" && x.item.id === ch.item.id ? { ...x, item: nr } : x)) })}
                                        onCopy={() => set({ children: [...value.children, { kind: "single", item: cloneSingle(ch.item) }] })}
                                        onDelete={() => set({ children: value.children.filter((x) => !(x.kind === "single" && x.item.id === ch.item.id)) })}
                                        onSave={(ruleData) => onSaveSingle && onSaveSingle(ruleData)}
                                        ruleList={ruleList}
                                        typesList={typesList}
                                        KPIList={KPIList}
                                        typeValuesList={typeValuesList}
                                        operatorOptions={operatorOptions}
                                        companiesOptions={companiesOptions}
                                        isInsideBlock={true}
                                        isInsideCompound={false}
                                        inheritedExcluded={value.excludedCompanies}
                                    />
                                ) : (
                                    <CompoundRuleCard
                                        value={{ ...ch.item, editable: value.editable && ch.item.editable }}
                                        onChange={(nr) => set({ children: value.children.map((x) => (x.kind === "compound" && x.item.id === ch.item.id ? { ...x, item: nr } : x)) })}
                                        onCopy={() => set({ children: [...value.children, { kind: "compound", item: cloneCompound(ch.item) }] })}
                                        onDelete={() => set({ children: value.children.filter((x) => !(x.kind === "compound" && x.item.id === ch.item.id)) })}
                                        onAddSingle={() => set({ children: value.children.map((x) => (x.kind === "compound" && x.item.id === ch.item.id ? { ...x, item: { ...x.item, rules: [...x.item.rules, emptySingle()] } } : x)) })}
                                        onSave={(compoundData) => onSaveCompound && onSaveCompound(compoundData)}
                                        onSaveSingle={(ruleData) => onSaveSingle && onSaveSingle(ruleData)}
                                        ruleList={ruleList}
                                        typesList={typesList}
                                        KPIList={KPIList}
                                        typeValuesList={typeValuesList}
                                        operatorOptions={operatorOptions}
                                        companiesOptions={companiesOptions}
                                        isInsideBlock={true}
                                        inheritedExcluded={value.excludedCompanies}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function TopToolbar({ onAddBlock, onAddRootCompound, onAddRootSingle, onExpandAll, onCollapseAll, onSaveAll, reportYearList, handleReportYearChange, selectedReportYear, operationsList, selectedOperation, handleOperationChange, modules, modulesFiltered, selectedModule, handleModuleChange, isLoading }) {
    // Verificar se ReportYearID e ModuleID estão selecionados
    const isFormReady = selectedReportYear && selectedModule;

    return (<> 
        <div className="toolbar">
            <div style={{ display: "flex", gap: 12 }}>
                <div className="field" style={{ width: "8rem" }}>
                    <label>Report Year</label>
                    <Select
                        name="reportYear"
                        options={reportYearList}
                        className="basic-select"
                        classNamePrefix="select"
                        value={reportYearList.find(x => x.value == selectedReportYear)}
                        onChange={handleReportYearChange}
                    />
                </div>
                <div className="field" style={{ width: "20rem" }}>
                    <label>Form</label>
                    <Select
                        name="operation"
                        options={operationsList}
                        className="basic-select"
                        classNamePrefix="select"
                        value={operationsList.find(x => x.value == selectedOperation) || null}
                        onChange={handleOperationChange}
                    />
                </div>
                <div className="field" style={{ width: "20rem" }}>
                    <label>Module</label>
                    <Select
                        name="module"
                        options={modulesFiltered.length > 0 ? modulesFiltered : modules}
                        className="basic-select"
                        classNamePrefix="select"
                        value={(modulesFiltered.length > 0 ? modulesFiltered : modules).find(x => x.value == selectedModule) || null}
                        onChange={handleModuleChange}
                    />
                </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" onClick={onAddBlock} type="button" disabled={!isFormReady}>+ Add New Block</button>
                <button className="btn" onClick={onAddRootCompound} type="button" disabled={!isFormReady}>+ Add Compound Rule</button>
                <button className="btn" onClick={onAddRootSingle} type="button" disabled={!isFormReady}>+ Add Single Rule</button>
                <button className="btn" onClick={onSaveAll} type="button" disabled={isLoading} style={{ backgroundColor: '#0f2f5f' }}><IoIosSave size={16} /> Save All</button>
            </div>
        </div>
        <div className="toolbar" style={{ justifyContent: "flex-end" }}>
            <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-outline" onClick={onExpandAll} type="button">▾ Expand All</button>
                <button className="btn btn-outline" onClick={onCollapseAll} type="button">▸ Collapse All</button>
            </div>
        </div>
    </>
    );
}

// ---------------- Main Component ----------------
export default function AutomatedReviewRules() {
    const [reportYearList, setReportYearList] = useState([]);
    const [selectedReportYear, setSelectedReportYear] = useState();
    const [subSectionKpis, setSubSectionKpis] = useState([]);
    const [typeValues, setTypeValues] = useState([]);
    const [rules, setRules] = useState([]);
    const [modules, setModules] = useState([]);
    const [modulesFiltered, setModulesFiltered] = useState([]);
    const [selectedModule, setSelectedModule] = useState();
    const [operationsList, setOperationsList] = useState([]);
    const [selectedOperation, setSelectedOperation] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [companiesOptions, setCompaniesOptions] = useState([]);

    const loadCompanies = async() => {
    try {
        const result = await Api.get('Company/getAll');

        if(result && result.data){
            const options = result.data.response.map(item => ({
                value: item.companyID,
                label: item.name
            }));

            setCompaniesOptions(options);
        }
    } catch (error){
        exceptionNotificationAPI(error);
    }
    };

    const typeOptions = [
        { value: 1, label: "Simple" },
        { value: 2, label: "Country" },
        { value: 3, label: "FuelType" },
        { value: 4, label: "CultureType" },
        { value: 5, label: "Year" },
        { value: 6, label: "Curing Barn" }
    ];

    const operatorOptions = [
        { value: 1, label: "+-" },
        { value: 2, label: "<" },
        { value: 3, label: "<>" },
        { value: 4, label: "=" },
        { value: 5, label: ">=" },
        { value: 6, label: "<=" },
        { value: 7, label: ">" }
    ];

    const RuleCompanyType = {
        Single: 1,
        Compound: 2,
        Block: 3
    };

    useEffect(() => {
        setIsLoading(true);
        var userObjectAux = JSON.parse(localStorage.getItem('@Thrive:user'));
        if(userObjectAux != null) {
            loadReportYearList(userObjectAux);
            loadTypeValues(userObjectAux);
            loadRules();
            loadCompanies();
        }

    }, []);

    useEffect(() => {
        if (selectedOperation && modules.length > 0) {
            let filtered = modules.filter(
                module => module.operationID == selectedOperation
            );
            setModulesFiltered(filtered);
        } else if (modules.length > 0) {
            setModulesFiltered(modules);
        }
    }, [selectedOperation, modules]);

    // Report year data
    const loadReportYearList = (userObjectAux) => {
        Api.get(`Dashboard/getAllCropsYears?selectedCulture=${userObjectAux.selectedCultureID}`).then((result) => {
            if (result && result.data) {
                let reportYearData = result.data?.response?.map(year => ({
                    value: year.reportYearID,
                    label: year.year
                }));
                setReportYearList(reportYearData);
                var reportYear = result.data.response.find(x => x.year == ("" + new Date().getFullYear()));
                if (reportYear != null) {
                    setSelectedReportYear(reportYear.reportYearID);
                    loadOperations(reportYear.reportYearID);
                    loadModules(reportYear.reportYearID);
                }
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    // Operations (Forms) data
    const loadOperations = (reportYearID) => {
        if (!reportYearID) return;
        Api.get(`Operation/getAll`).then((result) => {
            if (result && result.data) {
                let operationsData = result.data?.response
                    ?.filter(op => op.reportYearID == reportYearID)
                    ?.map(operation => ({
                        value: operation.operationID,
                        label: operation.description
                    }));
                setOperationsList(operationsData || []);
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    // Modules data
    const loadModules = (reportYearID) => {
        Api.get(`Dashboard/GetAllByReportYear?reportYearID=${reportYearID}`).then((result) => {
            if (result && result.data) {
                let moduleData = result.data?.response?.map(year => ({
                    value: year.moduleID,
                    label: year.description,
                    operationID: year.operationID
                }));
                setModules(moduleData);
                // Filtrar módulos baseado no form selecionado
                if (selectedOperation) {
                    let filtered = moduleData.filter(
                        module => module.operationID == selectedOperation
                    );
                    setModulesFiltered(filtered);
                } else {
                    setModulesFiltered(moduleData);
                }
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    // Types data
    const loadTypeValues = (userObjectAux) => {
        Api.get(`Rule/GetAllTypeValues?cultureID=${userObjectAux.selectedCultureID}`).then((result) => {
            if (result && result.data) {
                setTypeValues(result.data.response);
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    // Rules data
    const loadRules = () => {
        Api.get(`Rule/GetAllRules`).then((result) => {
            if (result && result.data) {
                let ruleData = result.data.response.map(rule => ({
                    value: rule.ruleID,
                    label: rule.description,
                    allowCalculation: rule.allowCalculation,
                    allowLowerLimit: rule.allowLowerLimit,
                    allowUpperLimit: rule.allowUpperLimit,
                    allowComparisonOperator: rule.allowComparisonOperator,
                    allowTargetValue: rule.allowTargetValue,
                    allowKpiLink: rule.allowKpiLink,
                    operatorsGroup: rule.operatorsGroup
                }));
                setRules(ruleData);
                setIsLoading(false);
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    const handleReportYearChange = (event) => {
        const selectedYear = event.value;
        setSelectedReportYear(selectedYear);
        setSelectedOperation(null);
        setSelectedModule(null);
        loadOperations(selectedYear);
        loadModules(selectedYear);
    };

    const handleOperationChange = (event) => {
        const selectedOperationID = event ? event.value : null;
        setSelectedOperation(selectedOperationID);
        setSelectedModule(null);
        // Filtrar módulos baseado no form selecionado
        if (selectedOperationID) {
            let filtered = modules.filter(
                module => module.operationID == selectedOperationID
            );
            setModulesFiltered(filtered);
        } else {
            setModulesFiltered(modules);
        }
    };

    const handleModuleChange = (event) => {
        const selectedModuleID = event.value;
        setSelectedModule(selectedModuleID);
        Api.get(`Rule/getAllSskByModule?reportYearID=${selectedReportYear}&moduleID=${selectedModuleID}`).then((result) => {
            if (result && result.data) {
                let sskData = result.data.response.map(ssk => ({
                    value: ssk.subSectionKPIID,
                    label: ssk.description,
                    columnType: ssk.columnType
                }));
                setSubSectionKpis(sskData);
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });

        // Carregar regras estruturadas quando o módulo for selecionado
        loadStructuredRules(selectedReportYear, selectedModuleID);
    };

    // Função para carregar regras estruturadas
    const loadStructuredRules = (reportYearID, moduleID) => {
        setIsLoading(true);
        Api.get(`Rule/getStructuredRules?reportYearID=${reportYearID}&moduleID=${moduleID}`).then((result) => {
            if (result && result.data) {
                const structuredData = result.data.response;

                // Converter dados estruturados para o formato do componente
                const blocks = structuredData.blocks.map(block => mapBlockToForm(block));
                const rootCompounds = structuredData.rootCompounds.map(compound => mapCompoundToForm(compound));
                const rootSingles = structuredData.rootSingles.map(rule => mapSingleToForm(rule));

                // Atualizar estados
                setBlocks(blocks);
                setRootCompounds(rootCompounds);
                setRootSingles(rootSingles);
                setIsLoading(false);
            }
        }).catch((error) => {
            exceptionNotificationAPI(error);
            setIsLoading(false);
        });
    };

    // Função para mapear Block do backend para formato do frontend
    const mapBlockToForm = (block) => {
        return {
            id: `block_${block.blockRuleRegisterID}`,
            blockRuleRegisterID: block?.blockRuleRegisterID || null,
            logicOperator: block.logicOperator,
            collapsed: false,
            editable: false, // Dados existentes vêm desabilitados
            discussion: block.discussion || "",
            targetSubSectionKPIID: block.targetSubSectionKPIID || null,
            targetTypeID: block.targetTypeID || null,
            targetTypeValueID: block.targetTypeValueID || null,
            excludedCompanies: (block.excludedCompanies || []).map(c => c.companyId),
            children: block.children.map(child => ({
                kind: child.kind,
                item: child.kind === 'single'
                    ? mapSingleToForm(child.singleRule)
                    : mapCompoundToForm(child.compoundRule)
            }))
        };
    };

    // Função para mapear Compound do backend para formato do frontend
    const mapCompoundToForm = (compound) => {
        return {
            id: `compound_${compound.compoundRuleRegisterID}`,
            compoundRuleRegisterID: compound?.compoundRuleRegisterID || null,
            logicOperator: compound.logicOperator,
            collapsed: false,
            editable: false, // Dados existentes vêm desabilitados
            discussion: compound.discussion || "",
            targetSubSectionKPIID: compound.targetSubSectionKPIID || null,
            targetTypeID: compound.targetTypeID || null,
            targetTypeValueID: compound.targetTypeValueID || null,
           excludedCompanies: (compound.excludedCompanies || []).map(c => c.companyId),
            rules: compound.rules.map(rule => mapSingleToForm(rule))
        };
    };

    // Função para mapear Single do backend para formato do frontend
    const mapSingleToForm = (rule) => {
        return {
            ruleRegisterID: rule?.ruleRegisterID || null,
            id: `single_${rule.ruleRegisterID}`,
            label: rule.ruleDesc || "",
            collapsed: false,
            editable: false, // Dados existentes vêm desabilitados
            kpi: rule.subSectionKPIID,
            type: rule.typeID,
            typeValue: rule.typeValueID,
            ruleTemplate: rule.ruleID,
            lowerLimit: parseFloat(rule.lowerLimit) || null,
            upperLimit: parseFloat(rule.upperLimit) || null,
            comparator: rule.comparisonOperator || null,
            linkedKPI: rule.linkedKPI || "",
            value: rule.targetValue || "",
            discussion: rule.discussion || "",
            excludedCompanies: (rule.excludedCompanies || []).map(c => c.companyId)

        };
    };

    // Função para converter dados do form.jsx para o formato da API
    const convertFormDataToApiFormat = (formData) => {
        return {
            blocks: (formData.blocks || []).map(block => {
                console.log('🔍 Processando block:', block);
                console.log('🔍 logicOperator value:', block.logicOperator, 'type:', typeof block.logicOperator);
                return {
                    blockRuleRegisterID: block.id.startsWith('block_') ?
                        parseInt(block.id.replace('block_', '')) : 0,
                    reportYearID: selectedReportYear,
                    moduleID: selectedModule,
                    operator: block.logicOperator === 1 ? "AND" : "OR",
                    logicOperator: block.logicOperator,
                    discussion: block.discussion || "",
                    targetSubSectionKPIID: block.targetSubSectionKPIID || null,
                    targetTypeID: block.targetTypeID || null,
                    targetTypeValueID: block.targetTypeValueID || null,
                    excludedCompanies: (block.excludedCompanies || []).map(companyID => ({
                        companyID,
                        type: RuleCompanyType.Block
                    })),
                    children: block.children.map(child => ({
                        kind: child.kind,
                        singleRule: child.kind === 'single' ? {
                            ruleRegisterID: child.item.id.startsWith('single_') ?
                                parseInt(child.item.id.replace('single_', '')) : 0,
                            reportYearID: selectedReportYear,
                            moduleID: selectedModule,
                            ruleID: child.item.ruleTemplate,
                            subSectionKPIID: child.item.kpi,
                            subSectionKPIRefNumber: child.item.subSectionKPIRefNumber || "",
                            subSectionKPIColumnDesc: child.item.subSectionKPIColumnDesc || "",
                            discussion: child.item.discussion || "",
                            lowerLimit: child.item.lowerLimit?.toString() || "",
                            upperLimit: child.item.upperLimit?.toString() || "",
                            comparisonOperator: child.item.comparator || null,
                            targetValue: child.item.value || "",
                            linkedKPI: child.item.linkedKPI || "",
                           
                            excludedCompanies: (child.item.excludedCompanies || []).map(companyID => ({
                                companyID,
                                type: RuleCompanyType.Single
                            })),
                            typeID: child.item.type,
                            typeValueID: child.item.typeValue,
                            groupIdentifier: child.item.groupIdentifier || null
                        } : null,
                        compoundRule: child.kind === 'compound' ? {
                            compoundRuleRegisterID: child.item.id.startsWith('compound_') ?
                                parseInt(child.item.id.replace('compound_', '')) : 0,
                            reportYearID: selectedReportYear,
                            moduleID: selectedModule,
                            operator: child.item.logicOperator === 1 ? "AND" : "OR",
                            logicOperator: child.item.logicOperator,
                            discussion: child.item.discussion || "",
                            targetSubSectionKPIID: child.item.targetSubSectionKPIID || null,
                            targetTypeID: child.item.targetTypeID || null,
                            targetTypeValueID: child.item.targetTypeValueID || null,
                            excludedCompanies: (child.item.excludedCompanies || []).map(companyID => ({
                                companyID,
                                type: RuleCompanyType.Compound
                            })),
                            rules: child.item.rules.map(rule => ({
                                ruleRegisterID: rule.id.startsWith('single_') ?
                                    parseInt(rule.id.replace('single_', '')) : 0,
                                reportYearID: selectedReportYear,
                                moduleID: selectedModule,
                                ruleID: rule.ruleTemplate,
                                subSectionKPIID: rule.kpi,
                                subSectionKPIRefNumber: rule.subSectionKPIRefNumber || "",
                                subSectionKPIColumnDesc: rule.subSectionKPIColumnDesc || "",
                                discussion: rule.discussion || "",
                                lowerLimit: rule.lowerLimit?.toString() || "",
                                upperLimit: rule.upperLimit?.toString() || "",
                                comparisonOperator: rule.comparator || null,
                                targetValue: rule.value || "",
                                linkedKPI: rule.linkedKPI || "",
                                typeID: rule.type,
                                typeValueID: rule.typeValue,
                                groupIdentifier: rule.groupIdentifier || null,
                                excludedCompanies: (rule.excludedCompanies || []).map(companyID => ({
                                companyID,
                                type: RuleCompanyType.Single
                                }))
                            }))
                        } : null
                    }))
                };
            }),
            rootCompounds: (formData.rootCompounds || []).map(compound => ({
                compoundRuleRegisterID: compound.id.startsWith('compound_') ?
                    parseInt(compound.id.replace('compound_', '')) : 0,
                reportYearID: selectedReportYear,
                moduleID: selectedModule,
                operator: compound.logicOperator === 1 ? "AND" : "OR",
                logicOperator: compound.logicOperator,
                discussion: compound.discussion || "",
                targetSubSectionKPIID: compound.targetSubSectionKPIID || null,
                targetTypeID: compound.targetTypeID || null,
                targetTypeValueID: compound.targetTypeValueID || null,
                excludedCompanies: (compound.excludedCompanies || []).map(companyID => ({
                    companyID,
                    type: RuleCompanyType.Compound
                })),
                rules: compound.rules.map(rule => ({
                    ruleRegisterID: rule.id.startsWith('single_') ?
                        parseInt(rule.id.replace('single_', '')) : 0,
                    reportYearID: selectedReportYear,
                    moduleID: selectedModule,
                    ruleID: rule.ruleTemplate,
                    subSectionKPIID: rule.kpi,
                    subSectionKPIRefNumber: rule.subSectionKPIRefNumber || "",
                    subSectionKPIColumnDesc: rule.subSectionKPIColumnDesc || "",
                    discussion: rule.discussion || "",
                    lowerLimit: rule.lowerLimit?.toString() || "",
                    upperLimit: rule.upperLimit?.toString() || "",
                    comparisonOperator: rule.comparator,
                    targetValue: rule.value || "",
                    linkedKPI: rule.linkedKPI || "",
                    typeID: rule.type,
                    typeValueID: rule.typeValue,
                    groupIdentifier: rule.groupIdentifier || null,
                    excludedCompanies: (rule.excludedCompanies || []).map(companyID => ({
                        companyID,
                        type: RuleCompanyType.Single
                        }))
                }))
            })),
            rootSingles: (formData.rootSingles || []).map(rule => ({
                ruleRegisterID: rule.id.startsWith('single_') ?
                    parseInt(rule.id.replace('single_', '')) : 0,
                reportYearID: selectedReportYear,
                moduleID: selectedModule,
                ruleID: rule.ruleTemplate,
                subSectionKPIID: rule.kpi,
                subSectionKPIRefNumber: rule.subSectionKPIRefNumber || "",
                subSectionKPIColumnDesc: rule.subSectionKPIColumnDesc || "",
                discussion: rule.discussion || "",
                lowerLimit: rule.lowerLimit?.toString() || "",
                upperLimit: rule.upperLimit?.toString() || "",
                comparisonOperator: rule.comparator,
                targetValue: rule.value || "",
                linkedKPI: rule.linkedKPI || "",
                typeID: rule.type,
                typeValueID: rule.typeValue,
                excludedCompanies: (rule.excludedCompanies || []).map(companyID => ({
                    companyID,
                    type: RuleCompanyType.Single
                })),
                groupIdentifier: rule.groupIdentifier || null
            }))
        };
    };

    // Função para salvar todas as regras
    const saveAllRules = async () => {
        setIsLoading(true);
        try {
            const apiData = convertFormDataToApiFormat({
                blocks: blocks,
                rootCompounds: rootCompounds,
                rootSingles: rootSingles
            });
            console.log('💾 Salvando todas as regras...', apiData);
            const response = await Api.post(`Rule/createUpdateStructuredRules?reportYearID=${selectedReportYear}&moduleID=${selectedModule}`, apiData);

            if (response && response.data) {
                console.log('✅ Todas as regras salvas com sucesso!');
                // Recarregar dados para obter IDs atualizados
                loadStructuredRules(selectedReportYear, selectedModule);
            }
        } catch (error) {
            console.error('❌ Erro ao salvar todas as regras:', error);
            exceptionNotificationAPI(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Função para salvar um block específico
    const saveBlock = async (blockData) => {
        try {
            console.log('🔍 Block data antes da conversão:', blockData);
            const apiData = convertFormDataToApiFormat({ blocks: [blockData] });
            console.log('💾 Salvando block...', apiData.blocks[0]);

            const response = await Api.post('Rule/createUpdateBlock', {
                blockData: apiData.blocks[0],
                reportYearID: selectedReportYear,
                moduleID: selectedModule
            });

            if (response && response.data) {
                console.log('✅ Block salvo com sucesso!');
                // Recarregar dados para obter IDs atualizados
                loadStructuredRules(selectedReportYear, selectedModule);
            }
            return response.data;
        } catch (error) {
            console.error('❌ Erro ao salvar block:', error);
            exceptionNotificationAPI(error);
            throw error;
        }
    };

    // Função para salvar uma regra simples
    const saveSingleRule = async (ruleData, compoundRuleRegisterID = null, blockRuleRegisterID = null) => {
        try {
            const singleDto = {
                ruleRegisterID: ruleData.id.startsWith('single_') ?
                    parseInt(ruleData.id.replace('single_', '')) : null,
                reportYearID: selectedReportYear,
                moduleID: selectedModule,
                ruleID: ruleData.ruleTemplate,
                subSectionKPIID: ruleData.kpi,
                subSectionKPIRefNumber: ruleData.subSectionKPIRefNumber || "",
                subSectionKPIColumnDesc: ruleData.subSectionKPIColumnDesc || "",
                discussion: ruleData.discussion || "",
                lowerLimit: ruleData.lowerLimit?.toString() || "",
                upperLimit: ruleData.upperLimit?.toString() || "",
                comparisonOperator: ruleData.comparator,
                targetValue: ruleData.value || "",
                linkedKPI: ruleData.linkedKPI || "",
                typeID: ruleData.type,
                typeValueID: ruleData.typeValue,
                excludedCompanies: (ruleData.excludedCompanies || []).map(companyID => ({
                    companyID, type: RuleCompanyType.Single })),
                compoundRuleRegisterID: compoundRuleRegisterID && compoundRuleRegisterID.startsWith('compound_') ?
                    parseInt(compoundRuleRegisterID.replace('compound_', '')) : null,
                blockRuleRegisterID: blockRuleRegisterID && blockRuleRegisterID.startsWith('block_') ?
                    parseInt(blockRuleRegisterID.replace('block_', '')) : null,
                groupIdentifier: ruleData.groupIdentifier || null
            };

            console.log('💾 Salvando regra simples...', singleDto);

            const response = await Api.post('Rule/createOrUpdateRule', singleDto);

            if (response && response.data) {
                console.log('✅ Regra simples salva com sucesso!');
                // Recarregar dados para obter IDs atualizados
                loadStructuredRules(selectedReportYear, selectedModule);
            }
            return response.data;
        } catch (error) {
            console.error('❌ Erro ao salvar regra simples:', error);
            exceptionNotificationAPI(error);
            throw error;
        }
    };

    // Função para salvar uma regra composta
    const saveCompoundRule = async (compoundData, blockId) => {
        try {
            // Preparar dados da compound rule
            const compoundDto = {
                compoundRuleRegisterID: compoundData.id.startsWith('compound_') ?
                    parseInt(compoundData.id.replace('compound_', '')) : null,
                reportYearID: selectedReportYear,
                moduleID: selectedModule,
                blockRuleRegisterID: blockId && blockId.startsWith('block_') ?
                    parseInt(blockId.replace('block_', '')) : null,
                logicOperator: compoundData.logicOperator, // Já vem como número (1 ou 2)
                discussion: compoundData.discussion || "",
                targetSubSectionKPIID: compoundData.targetSubSectionKPIID || null,
                targetTypeID: compoundData.targetTypeID || null,
                targetTypeValueID: compoundData.targetTypeValueID || null,
                excludedCompanies: (compoundData.excludedCompanies || []).map(companyID => ({
                    companyID, type: RuleCompanyType.Compound
                })),
                compoundType: 1,
                groupIdentifier: compoundData.groupIdentifier || null,
                // Incluir as regras simples para o backend processar
                rulesDetails: compoundData.rules.map(rule => ({
                    ruleRegisterID: rule.id.startsWith('single_') ?
                        parseInt(rule.id.replace('single_', '')) : null,
                    reportYearID: selectedReportYear,
                    moduleID: selectedModule,
                    ruleID: rule.ruleTemplate,
                    subSectionKPIID: rule.kpi,
                    subSectionKPIRefNumber: rule.subSectionKPIRefNumber || "",
                    subSectionKPIColumnDesc: rule.subSectionKPIColumnDesc || "",
                    discussion: rule.discussion || "",
                    lowerLimit: rule.lowerLimit?.toString() || "",
                    upperLimit: rule.upperLimit?.toString() || "",
                    comparisonOperator: rule.comparator,
                    targetValue: rule.value || "",
                    linkedKPI: rule.linkedKPI || "",
                    typeID: rule.type,
                    typeValueID: rule.typeValue,
                    groupIdentifier: rule.groupIdentifier || null,
                    excludedCompanies: (rule.excludedCompanies || []).map(companyID => ({
                        companyID,
                        type: RuleCompanyType.Single
                    }))
                }))
            };

            console.log('💾 Enviando compound rule com rules para o backend...', compoundDto);
            console.log(JSON.stringify(compoundDto, null, 2));
            // Enviar tudo para o backend processar
            const response = await Api.post('Rule/createOrUpdateCompoundRule', compoundDto);

            if (response && response.data) {
                console.log('✅ Compound rule e suas rules processadas pelo backend com sucesso!');
                // Recarregar dados para obter IDs atualizados
                await loadStructuredRules(selectedReportYear, selectedModule);
            }

            return response.data;
        } catch (error) {
            console.error('❌ Erro ao salvar regra composta:', error);
            exceptionNotificationAPI(error);
            throw error;
        }
    };



    const [blocks, setBlocks] = useState(() => []);
    const [rootSingles, setRootSingles] = useState(() => []);
    const [rootCompounds, setRootCompounds] = useState(() => []);

    // ---- Blocks ----
    const updateBlock = (id, patcher) => setBlocks((arr) => arr.map((b) => (b.id === id ? patcher(b) : b)));
    const addBlock = () => {
        const newBlock = emptyBlock();
        setBlocks((arr) => [...arr, newBlock]);
        // Scroll para o novo block após um pequeno delay para garantir que o DOM foi atualizado
        setTimeout(() => {
            const element = document.getElementById(`block-${newBlock.id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };
    const copyBlock = (b) => setBlocks((arr) => [...arr, cloneBlock(b)]);
    const deleteBlock = async (id) => {
        const block = blocks.find(b => b.id === id);
        if (!block) return;

        // Extrair o ID numérico do block
        const blockId = block.id.startsWith('block_') ?
            parseInt(block.id.replace('block_', '')) :
            (block.blockRuleRegisterID || 0);

        if (blockId === 0) {
            // Se não tem ID, apenas remove da lista (block não salvo)
            setBlocks((arr) => arr.filter((b) => b.id !== id));
            return;
        }

        // Mostrar modal de confirmação
        const result = await Swal.fire({
            title: 'Delete Block',
            text: 'Are you sure you want to delete this block? This action will also delete all single rules and compound rules inside it.',
            icon: 'warning',
            showDenyButton: true,
            confirmButtonColor: '#d33',
            denyButtonColor: '#3085d6',
            denyButtonText: 'Cancel',
            confirmButtonText: 'Yes, delete it!',
            showCloseButton: true,
        });

        if (result.isConfirmed) {
            try {
                const response = await Api.post(`Rule/deleteBlock?blockID=${blockId}`);
                if (response) {
                    Swal.fire('Deleted!', 'The block has been deleted successfully.', 'success');
                    setBlocks((arr) => arr.filter((b) => b.id !== id));
                } else {
                    Swal.fire('Error!', 'Failed to delete the block.', 'error');
                }
            } catch (error) {
                console.error('Error deleting block:', error);
                Swal.fire('Error!', 'An error occurred while deleting the block.', 'error');
            }
        }
    };

    // ---- Root Single ----
    const addRootSingle = () => {
        const newSingle = emptySingle();
        setRootSingles((arr) => [...arr, newSingle]);
        // Scroll para o novo single rule após um pequeno delay para garantir que o DOM foi atualizado
        setTimeout(() => {
            const element = document.getElementById(`single-${newSingle.id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };
    const updateRootSingle = (id, patcher) => setRootSingles((arr) => arr.map((s) => (s.id === id ? patcher(s) : s)));
    const copyRootSingle = (s) => setRootSingles((arr) => [...arr, cloneSingle(s)]);
    const deleteRootSingle = async (id) => {
        const rule = rootSingles.find(s => s.id === id);
        if (!rule) return;

        // Extrair o ID numérico da regra
        const ruleId = rule.id.startsWith('single_') ?
            parseInt(rule.id.replace('single_', '')) :
            (rule.ruleRegisterID || 0);

        if (ruleId === 0) {
            // Se não tem ID, apenas remove da lista (regra não salva)
            setRootSingles((arr) => arr.filter((s) => s.id !== id));
            return;
        }

        // Mostrar modal de confirmação
        const result = await Swal.fire({
            title: 'Delete Rule',
            text: 'Are you sure you want to delete this rule? This action will also remove it from any compound rules or blocks that contain it.',
            icon: 'warning',
            showDenyButton: true,
            confirmButtonColor: '#d33',
            denyButtonColor: '#3085d6',
            denyButtonText: 'Cancel',
            confirmButtonText: 'Yes, delete it!',
            showCloseButton: true,
        });

        if (result.isConfirmed) {
            try {
                const response = await Api.post(`Rule/deleteSimpleRule?ruleID=${ruleId}`);
                if (response) {
                    Swal.fire('Deleted!', 'The rule has been deleted successfully.', 'success');
                    setRootSingles((arr) => arr.filter((s) => s.id !== id));
                } else {
                    Swal.fire('Error!', 'Failed to delete the rule.', 'error');
                }
            } catch (error) {
                console.error('Error deleting rule:', error);
                Swal.fire('Error!', 'An error occurred while deleting the rule.', 'error');
            }
        }
    };

    // ---- Root Compound ----
    const addRootCompound = () => {
        const newCompound = emptyCompound();
        setRootCompounds((arr) => [...arr, newCompound]);
        // Scroll para o novo compound rule após um pequeno delay para garantir que o DOM foi atualizado
        setTimeout(() => {
            const element = document.getElementById(`compound-${newCompound.id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };
    const updateRootCompound = (id, patcher) => setRootCompounds((arr) => arr.map((c) => (c.id === id ? patcher(c) : c)));
    const copyRootCompound = (c) => setRootCompounds((arr) => [...arr, cloneCompound(c)]);
    const deleteRootCompound = async (id) => {
        const compound = rootCompounds.find(c => c.id === id);
        if (!compound) return;

        // Extrair o ID numérico da compound rule
        const compoundId = compound.id.startsWith('compound_') ?
            parseInt(compound.id.replace('compound_', '')) :
            (compound.compoundRuleRegisterID || 0);

        if (compoundId === 0) {
            // Se não tem ID, apenas remove da lista (compound não salva)
            setRootCompounds((arr) => arr.filter((c) => c.id !== id));
            return;
        }

        // Mostrar modal de confirmação
        const result = await Swal.fire({
            title: 'Delete Compound Rule',
            text: 'Are you sure you want to delete this compound rule? This action will also delete all single rules inside it and remove it from any blocks that contain it.',
            icon: 'warning',
            showDenyButton: true,
            confirmButtonColor: '#d33',
            denyButtonColor: '#3085d6',
            denyButtonText: 'Cancel',
            confirmButtonText: 'Yes, delete it!',
            showCloseButton: true,
        });

        if (result.isConfirmed) {
            try {
                const response = await Api.post(`Rule/deleteCompoundRule?ruleID=${compoundId}`);
                if (response) {
                    Swal.fire('Deleted!', 'The compound rule has been deleted successfully.', 'success');
                    setRootCompounds((arr) => arr.filter((c) => c.id !== id));
                } else {
                    Swal.fire('Error!', 'Failed to delete the compound rule.', 'error');
                }
            } catch (error) {
                console.error('Error deleting compound rule:', error);
                Swal.fire('Error!', 'An error occurred while deleting the compound rule.', 'error');
            }
        }
    };

    // ---- Expand/Collapse All ----
    const expandAll = () => {
        setRootSingles((arr) => arr.map((r) => ({ ...r, collapsed: false })));
        setRootCompounds((arr) => arr.map((c) => ({ ...c, collapsed: false, rules: c.rules.map((r) => ({ ...r, collapsed: false })) })));
        setBlocks((arr) => arr.map((b) => ({
            ...b,
            collapsed: false,
            children: b.children.map((ch) =>
                ch.kind === "single"
                    ? { kind: "single", item: { ...ch.item, collapsed: false } }
                    : { kind: "compound", item: { ...ch.item, collapsed: false, rules: ch.item.rules.map((r) => ({ ...r, collapsed: false })) } }
            ),
        })));
    };

    const collapseAll = () => {
        setRootSingles((arr) => arr.map((r) => ({ ...r, collapsed: true })));
        setRootCompounds((arr) => arr.map((c) => ({ ...c, collapsed: true, rules: c.rules.map((r) => ({ ...r, collapsed: true })) })));
        setBlocks((arr) => arr.map((b) => ({
            ...b,
            collapsed: true,
            children: b.children.map((ch) =>
                ch.kind === "single"
                    ? { kind: "single", item: { ...ch.item, collapsed: true } }
                    : { kind: "compound", item: { ...ch.item, collapsed: true, rules: ch.item.rules.map((r) => ({ ...r, collapsed: true })) } }
            ),
        })));
    };

    // ---- Stats ----
    const stats = useMemo(() => {
        const singlesInBlocks = blocks.reduce((acc, b) => acc + b.children.reduce((a, ch) => a + (ch.kind === "single" ? 1 : ch.item.rules.length), 0), 0);
        const compoundsInBlocks = blocks.reduce((acc, b) => acc + b.children.filter((c) => c.kind === "compound").length, 0);
        const singlesInRoot = rootSingles.length + rootCompounds.reduce((a, c) => a + c.rules.length, 0);
        const compoundsInRoot = rootCompounds.length;
        return { singleCount: singlesInBlocks + singlesInRoot, compoundCount: compoundsInBlocks + compoundsInRoot, blockCount: blocks.length };
    }, [blocks, rootSingles, rootCompounds]);


    return (
        isLoading ?
            <TableLoading /> :
            <FormSAR>
                <div className="container">
                    <h1 className="arp-title">Automated Review Rules</h1>
                    <div className="arp-sub">Blocks: {stats.blockCount} · Compound Rules: {stats.compoundCount} · Single Rules: {stats.singleCount}</div>

                    <TopToolbar
                        onAddBlock={addBlock}
                        onAddRootCompound={addRootCompound}
                        onAddRootSingle={addRootSingle}
                        onExpandAll={expandAll}
                        onCollapseAll={collapseAll}
                        onSaveAll={saveAllRules}
                        reportYearList={reportYearList}
                        operationsList={operationsList}
                        selectedOperation={selectedOperation}
                        handleOperationChange={handleOperationChange}
                        modules={modules}
                        modulesFiltered={modulesFiltered}
                        selectedReportYear={selectedReportYear}
                        selectedModule={selectedModule}
                        handleReportYearChange={handleReportYearChange}
                        handleModuleChange={handleModuleChange}
                        isLoading={isLoading}
                    />

                    {/* Regras no nível raiz (fora de Block) */}
                    {(rootCompounds.length > 0 || rootSingles.length > 0) && (
                        <div style={{ marginBottom: 10 }}>
                            <div className="section-title">Root Rules (fora de Block)</div>
                            <div style={{ display: "grid", gap: 12 }}>
                                {rootCompounds.map((c) => (
                                    <CompoundRuleCard
                                        key={c.id}
                                        value={c}
                                        onChange={(nc) => updateRootCompound(c.id, () => nc)}
                                        onCopy={() => copyRootCompound(c)}
                                        onDelete={() => deleteRootCompound(c.id)}
                                        onAddSingle={() => updateRootCompound(c.id, (cc) => ({ ...cc, rules: [...cc.rules, emptySingle()] }))}
                                        onSave={(compoundData) => saveCompoundRule(compoundData)}
                                        onSaveSingle={(ruleData) => saveSingleRule(ruleData, c.id)}
                                        ruleList={rules}
                                        typesList={typeOptions}
                                        KPIList={subSectionKpis}
                                        typeValuesList={typeValues}
                                        operatorOptions={operatorOptions}
                                        companiesOptions={companiesOptions}
                                        isInsideBlock={false}
                                    />
                                ))}
                                {rootSingles.map((s) => (
                                    <SingleRuleCard
                                        key={s.id}
                                        value={s}
                                        onChange={(ns) => updateRootSingle(s.id, () => ns)}
                                        onCopy={() => copyRootSingle(s)}
                                        onDelete={() => deleteRootSingle(s.id)}
                                        onSave={(ruleData) => saveSingleRule(ruleData)}
                                        ruleList={rules}
                                        typesList={typeOptions}
                                        KPIList={subSectionKpis}
                                        typeValuesList={typeValues}
                                        operatorOptions={operatorOptions}
                                        isInsideBlock={false}
                                        companiesOptions={companiesOptions}
                                        isInsideCompound={false}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Blocks */}
                    <div style={{ display: "grid", gap: 12 }}>
                        {blocks.map((block) => (
                            <BlockCard
                                key={block.id}
                                value={block}
                                onChange={(nb) => updateBlock(block.id, () => nb)}
                                onCopy={() => copyBlock(block)}
                                onDelete={() => deleteBlock(block.id)}
                                onAddSingle={() => updateBlock(block.id, (b) => ({ ...b, children: [...b.children, { kind: "single", item: emptySingle() }] }))}
                                onAddCompound={() => updateBlock(block.id, (b) => ({ ...b, children: [...b.children, { kind: "compound", item: emptyCompound() }] }))}
                                onSave={(blockData) => saveBlock(blockData)}
                                onSaveSingle={(ruleData) => saveSingleRule(ruleData, null, block.id)}
                                onSaveCompound={(compoundData) => saveCompoundRule(compoundData, block.id)}
                                ruleList={rules}
                                typesList={typeOptions}
                                KPIList={subSectionKpis}
                                typeValuesList={typeValues}
                                operatorOptions={operatorOptions}
                                companiesOptions={companiesOptions}
                            />
                        ))}
                    </div>
                </div>
            </FormSAR>
    );
}
